/**
 * DiffLinkerInputs.jsx
 * Right-panel controls specific to the DiffLinker SBDD workflow.
 *
 * Props (all controlled by GenChemUpload):
 *   fragmentSource      'auto_ligand' | 'upload'
 *   onFragmentSource    fn(val)
 *   fragmentsFile       File | null
 *   onFragmentsFile     fn(File | null)
 *   anchorMode          'auto' | 'manual'
 *   onAnchorMode        fn(val)
 *   anchorIndices       string  e.g. '3,7'
 *   onAnchorIndices     fn(string)
 *   anchorError         string  validation message
 *   linkerSizeMode      'auto' | 'manual'
 *   onLinkerSizeMode    fn(val)
 *   linkerSize          number  3–10
 *   onLinkerSize        fn(number)
 *   nSamples            number  10–500
 *   onNSamples          fn(number)
 *   fragmentsInputRef   ref
 */

import React from 'react';
import {
  Upload, FlaskConical, CheckCircle2, X,
  Info, Link2, Maximize2, Hash,
} from 'lucide-react';
import { s, ToggleBtn, ModeSelector } from './genchemShared';

// Validate anchor indices: comma-separated positive integers
function validateAnchorIndices(val) {
  if (!val.trim()) return 'Anchor indices are required in manual mode.';
  const parts = val.split(',').map((p) => p.trim());
  if (parts.some((p) => !/^\d+$/.test(p) || parseInt(p, 10) < 1))
    return 'Enter comma-separated positive integers (1-indexed atom positions), e.g. 3,7';
  if (parts.length < 2)
    return 'At least 2 anchor atoms are required to define the two fragment attachment points.';
  return '';
}

export default function DiffLinkerInputs({
  fragmentSource,
  onFragmentSource,
  fragmentsFile,
  onFragmentsFile,
  anchorMode,
  onAnchorMode,
  anchorIndices,
  onAnchorIndices,
  anchorError,
  linkerSizeMode,
  onLinkerSizeMode,
  linkerSize,
  onLinkerSize,
  nSamples,
  onNSamples,
  fragmentsInputRef,
}) {
  function handleFragmentSourceChange(val) {
    onFragmentSource(val);
    if (val === 'auto_ligand') {
      onFragmentsFile(null);
      if (fragmentsInputRef.current) fragmentsInputRef.current.value = '';
    }
  }

  function handleLinkerSizeChange(e) {
    const val = Math.min(10, Math.max(3, parseInt(e.target.value, 10) || 3));
    onLinkerSize(val);
  }

  function handleNSamplesChange(e) {
    const val = Math.min(500, Math.max(10, parseInt(e.target.value, 10) || 10));
    onNSamples(val);
  }

  const anchorValidationMsg = anchorMode === 'manual' && anchorIndices
    ? validateAnchorIndices(anchorIndices)
    : '';

  return (
    <>
      {/* ── Fragment source ── */}
      <div style={s.card}>
        <p style={s.cardLabel}>Fragment source</p>
        <p style={s.cardSubLabel}>
          Provide two molecular fragments to connect, or let DiffLinker
          auto-detect them from the co-crystallised ligand.
        </p>
        <div style={s.toggleRow}>
          <ToggleBtn
            active={fragmentSource === 'auto_ligand'}
            onClick={() => handleFragmentSourceChange('auto_ligand')}
            icon={<FlaskConical size={14} strokeWidth={1.8} />}
            label="Auto-detect"
          />
          <ToggleBtn
            active={fragmentSource === 'upload'}
            onClick={() => handleFragmentSourceChange('upload')}
            icon={<Upload size={14} strokeWidth={1.8} />}
            label="Upload fragments"
          />
        </div>

        {fragmentSource === 'auto_ligand' && (
          <div style={s.infoNote}>
            <Info size={12} color="#7c5cbf" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              DiffLinker will identify a cleavable bond in the co-crystallised ligand
              and treat the resulting two fragments as the input scaffolds.
              The cut bond is reported in the job result.
            </span>
          </div>
        )}

        {fragmentSource === 'upload' && (
          <div style={s.uploadArea} onClick={() => fragmentsInputRef.current?.click()}>
            <input
              ref={fragmentsInputRef}
              type="file"
              accept=".sdf,.mol,.mol2,.pdb"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onFragmentsFile(file);
              }}
            />
            {fragmentsFile ? (
              <div style={s.fileRow}>
                <CheckCircle2 size={14} color="#7c5cbf" />
                <span style={s.fileName}>{fragmentsFile.name}</span>
                <button
                  style={s.clearBtn}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onFragmentsFile(null);
                    if (fragmentsInputRef.current) fragmentsInputRef.current.value = '';
                  }}
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <div style={s.uploadPrompt}>
                <Upload size={15} color="#9d84d8" strokeWidth={1.6} />
                <span style={s.uploadText}>
                  Upload fragments file{' '}
                  <span style={s.uploadExt}>.sdf / .mol / .mol2 / .pdb</span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Anchor atoms ── */}
      <div style={s.card}>
        <div style={s.cardHeaderRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link2 size={13} color="#7c5cbf" strokeWidth={2} />
            <p style={s.cardLabel}>Anchor atoms</p>
          </div>
        </div>
        <p style={s.cardSubLabel}>
          Anchor atoms define where the linker attaches to each fragment.
          Auto mode detects them from the cut bond; manual mode lets you
          specify exact atom positions.
        </p>
        <ModeSelector
          value={anchorMode}
          onChange={onAnchorMode}
          options={[
            { value: 'auto',   label: 'Auto-detect' },
            { value: 'manual', label: 'Manual' },
          ]}
        />

        {anchorMode === 'manual' && (
          <div style={s.inputWrap}>
            <div style={s.instructionBox}>
              <Info size={12} color="#92700a" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Enter <strong>1-indexed atom positions</strong> for the two attachment
                points — one per fragment, comma-separated.{' '}
                <em>Example: <code style={s.codeInline}>3,7</code></em>{' '}
                means atom 3 on fragment 1 and atom 7 on fragment 2. These positions
                will be highlighted in the 3D viewer.
              </span>
            </div>
            <input
              style={{
                ...s.textInput,
                borderColor: anchorValidationMsg ? '#e05252'
                  : anchorIndices ? '#b9a9e4' : '#e2ddf0',
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: '0.04em',
              }}
              type="text"
              placeholder="e.g. 3,7"
              value={anchorIndices}
              onChange={(e) => onAnchorIndices(e.target.value)}
              spellCheck={false}
            />
            {anchorValidationMsg && (
              <p style={s.errorMsg}>{anchorValidationMsg}</p>
            )}
            {!anchorValidationMsg && anchorIndices && (
              <p style={s.inputHint}>
                Atoms <strong>{anchorIndices}</strong> will be highlighted in the viewer
              </p>
            )}
          </div>
        )}

        {anchorMode === 'auto' && (
          <div style={s.infoNote}>
            <Info size={12} color="#7c5cbf" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Anchor atoms will be inferred automatically from the fragment
              attachment geometry. No manual selection needed.
            </span>
          </div>
        )}
      </div>

      {/* ── Linker size ── */}
      <div style={s.card}>
        <div style={s.cardHeaderRow}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Maximize2 size={13} color="#7c5cbf" strokeWidth={2} />
            <p style={s.cardLabel}>Linker size</p>
          </div>
        </div>
        <p style={s.cardSubLabel}>
          Controls the number of heavy atoms in the generated linker.
          Auto mode lets the model choose based on the binding pocket geometry.
        </p>
        <ModeSelector
          value={linkerSizeMode}
          onChange={onLinkerSizeMode}
          options={[
            { value: 'auto',   label: 'Auto' },
            { value: 'manual', label: 'Fixed size' },
          ]}
        />

        {linkerSizeMode === 'manual' && (
          <div style={s.inputWrap}>
            <div style={s.instructionBox}>
              <Info size={12} color="#92700a" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Specify the exact number of heavy atoms in the linker chain.{' '}
                Typical drug-like linkers span <strong>3 – 10 atoms</strong>.
                Shorter linkers produce more rigid scaffolds; longer ones
                allow more flexibility and conformational sampling.
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                style={{
                  ...s.numberInput,
                  width: 80,
                  borderColor: linkerSize < 3 || linkerSize > 10 ? '#e05252' : '#b9a9e4',
                }}
                type="number"
                min={3}
                max={10}
                value={linkerSize}
                onChange={handleLinkerSizeChange}
              />
              <span style={{ fontSize: 12, color: '#888' }}>
                heavy atoms (range: 3 – 10)
              </span>
            </div>
          </div>
        )}

        {linkerSizeMode === 'auto' && (
          <div style={s.infoNote}>
            <Info size={12} color="#7c5cbf" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              The model will determine the optimal linker length from the
              inter-fragment distance in the binding pocket.
            </span>
          </div>
        )}
      </div>

      {/* ── Samples to generate ── */}
      <div style={s.card}>
        <div style={s.cardHeaderRow}>
          <p style={s.cardLabel}>Samples to generate</p>
          <span style={s.badge}>{nSamples}</span>
        </div>
        <p style={s.cardSubLabel}>
          Number of linked molecules the model will generate. Higher counts
          increase chemical diversity at the cost of longer runtime.
        </p>
        <input
          style={{
            ...s.numberInput,
            borderColor: nSamples < 10 || nSamples > 500 ? '#e05252' : '#e2ddf0',
          }}
          type="number"
          min={10}
          max={500}
          value={nSamples}
          onChange={handleNSamplesChange}
        />
        <p style={s.inputHint}>Range: 10 – 500 samples per job</p>
      </div>
    </>
  );
}